<template>
    <section class="flex flex-col gap-5">
        <TeamFeedbackAnimation v-if="sending" @done="onSendAnimationDone" />

        <!-- Feedback submitted state — the prototype drops the header here so the celebration stands alone. -->
        <div v-else-if="submitted" class="flex flex-col items-center gap-4 text-center pt-6">
            <span class="submitted-badge">
                <i class="bi bi-check-lg" aria-hidden="true"></i>
            </span>
            <div class="flex flex-col gap-2 max-w-md">
                <h2 class="display-serif text-valence-stone-900">Feedback submitted</h2>
                <p class="body-small text-valence-stone-500">
                    Thanks — your feedback for {{ subjectFirstName || "your teammate" }} is in. It stays anonymous and helps shape their 360.
                </p>
            </div>
            <BaseButton theme="primary" size="sm" class="base-btn-dark" @click="emit('back')">Done</BaseButton>
        </div>

        <template v-else>
            <div class="flex flex-col gap-2">
                <div class="flex items-center gap-3 flex-wrap">
                    <ReflectionIcon kind="team" size="md" />
                    <h2 class="title-subsection text-valence-stone-900">Team Reflections</h2>
                </div>
                <p class="body-small text-valence-stone-500">Give the 360 feedback your teammates have requested — your input shapes their reflection.</p>
            </div>

            <hr class="border-t border-valence-cream-300" />

            <button type="button" class="body-small text-valence-stone-500 hover:text-valence-stone-900 flex items-center gap-1 self-start" @click="emit('back')">
                <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
            </button>

            <div v-if="loading" class="body-small text-valence-stone-500">Loading…</div>
            <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

            <div v-else-if="isGrouped" class="flex flex-col gap-2.5">
                <div v-if="subjectFirstName" class="scaffold-details-small text-valence-stone-500">Give 360 feedback to {{ subjectFirstName }}</div>

                <AssessmentGroupedSections
                    :response-id="responseId"
                    :questions="questions"
                    :sections="sections"
                    :allow-score-examples="allowScoreExamples"
                    :allow-not-observed="allowNotObserved"
                    :accent-color="ACCENT_MINT"
                    @submitted="onGroupedSubmitted"
                    @exit="emit('back')" />
            </div>

            <template v-else-if="currentQuestion">
                <div class="flex flex-col gap-2.5">
                    <div v-if="subjectFirstName" class="scaffold-details-small text-valence-stone-500">Give 360 feedback to {{ subjectFirstName }}</div>

                    <QuestionCard
                        :question="currentQuestion"
                        :score="currentSavedScore"
                        :text="currentTextDraft"
                        :not-observed="currentSavedNotObserved"
                        :allow-score-examples="allowScoreExamples"
                        :allow-not-observed="allowNotObserved"
                        :show-unanswered="showValidation && isCurrentUnanswered"
                        :accent-color="ACCENT_MINT"
                        accent-surface-color="var(--color-valence-mint-100)"
                        @update:score="setScore"
                        @update:text="currentTextDraft = $event"
                        @update:not-observed="setNotObserved"
                        @update:busy="composerBusy = $event" />
                </div>

                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

                <div class="flex items-center justify-between">
                    <button
                        type="button"
                        :disabled="currentIndex === 0 || saving || composerBusy"
                        class="body-small text-valence-stone-500 hover:text-valence-stone-900 disabled:opacity-30 flex items-center gap-1"
                        @click="goPrev">
                        <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
                    </button>

                    <BaseButton v-if="!isLast" theme="primary" class="base-btn-dark" :disabled="saving || composerBusy" @click="goNext"> Next </BaseButton>
                    <BaseButton v-else theme="primary" class="base-btn-dark" :disabled="submitting || saving || composerBusy" @click="askToSubmit">
                        {{ submitting ? "Submitting…" : "Submit Feedback" }}
                    </BaseButton>
                </div>

                <div class="body-small text-valence-stone-500 text-center">Question {{ currentIndex + 1 }} of {{ questions.length }}</div>
            </template>

            <SubmitConfirmDialog
                v-model:open="showSubmitConfirm"
                title="Submit your responses?"
                text="Once you submit, your responses are final and cannot be changed"
                :confirm-disabled="submitting"
                @confirm="submit" />
        </template>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AssessmentDisplayMode, QuestionWithSaved, ResponseWithQuestions, SectionSummary } from "~vue/api/generated/reflections";
import AssessmentGroupedSections from "~vue/components/AssessmentGroupedSections.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import QuestionCard from "~vue/components/QuestionCard.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import SubmitConfirmDialog from "~vue/pages/reflections/SubmitConfirmDialog.vue";
import TeamFeedbackAnimation from "~vue/pages/reflections/TeamFeedbackAnimation.vue";
import { isRequiredUnanswered } from "~vue/utils/assessmentValidation";
import { computed, onMounted, ref } from "vue";

interface Props {
    inviteToken: string;
    subjectFirstName?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{ (event: "back"): void; (event: "submitted"): void }>();

// Mint accent of the redesigned team reflections family.
const ACCENT_MINT = "#bee59f";

const questions = ref<QuestionWithSaved[]>([]);
const sections = ref<SectionSummary[]>([]);
const responseId = ref<string>("");
const allowScoreExamples = ref(true);
const allowNotObserved = ref(true);
const displayMode = ref<AssessmentDisplayMode>("one_per_page");
const isGrouped = computed(() => displayMode.value === "grouped_by_section");
const currentIndex = ref(0);
const currentTextDraft = ref<string>("");
const loading = ref(true);
const loadError = ref<string | null>(null);
const error = ref<string | null>(null);
const submitting = ref(false);
const saving = ref(false);
const submitted = ref(false);
const sending = ref(false);
const showValidation = ref(false);
const showSubmitConfirm = ref(false);
const composerBusy = ref(false);

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/invites/${props.inviteToken}/response`).post().json();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = (data.value as { error?: string })?.error || "Could not load feedback questions.";
        return;
    }
    const payload = (data.value as { response: ResponseWithQuestions }).response;
    questions.value = payload.questions;
    sections.value = payload.sections ?? [];
    responseId.value = payload.id;
    allowScoreExamples.value = payload.allow_score_examples ?? true;
    allowNotObserved.value = payload.allow_not_observed ?? true;
    displayMode.value = payload.display_mode ?? "one_per_page";
    currentTextDraft.value = payload.questions[0]?.saved.text ?? "";
});

const currentQuestion = computed(() => questions.value[currentIndex.value]);
const currentSavedScore = computed(() => currentQuestion.value?.saved.score ?? null);
const currentSavedNotObserved = computed(() => currentQuestion.value?.saved.not_observed ?? false);
const isLast = computed(() => currentIndex.value === questions.value.length - 1);
const isCurrentUnanswered = computed(() => {
    if (!currentQuestion.value) {
        return false;
    }
    return isRequiredUnanswered(currentQuestion.value);
});

function setScore(n: number) {
    if (!currentQuestion.value) {
        return;
    }
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score: n, not_observed: false };
}
function setNotObserved(v: boolean) {
    if (!currentQuestion.value) {
        return;
    }
    const score = v ? null : currentQuestion.value.saved.score;
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score, not_observed: v };
}

async function saveCurrent(): Promise<boolean> {
    if (!currentQuestion.value) {
        return true;
    }
    if (currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    saving.value = true;
    error.value = null;
    try {
        const { error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/answers`)
            .post(
                JSON.stringify({
                    question_id: currentQuestion.value.id,
                    score: currentQuestion.value.saved.score,
                    text: currentQuestion.value.saved.text,
                    not_observed: currentQuestion.value.saved.not_observed,
                }),
                "application/json",
            )
            .json();
        if (fetchError.value) {
            error.value = "Could not save your answer. Please try again.";
            return false;
        }
        return true;
    } finally {
        saving.value = false;
    }
}

function validate(): boolean {
    if (currentQuestion.value && currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    if (currentQuestion.value && isRequiredUnanswered(currentQuestion.value)) {
        showValidation.value = true;
        return false;
    }
    showValidation.value = false;
    return true;
}

async function goNext() {
    if (!validate()) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    if (currentIndex.value < questions.value.length - 1) {
        currentIndex.value += 1;
        currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
    }
}

async function goPrev() {
    if (currentIndex.value === 0) {
        emit("back");
        return;
    }
    await saveCurrent();
    currentIndex.value -= 1;
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
}

function onSendAnimationDone() {
    sending.value = false;
    submitted.value = true;
}

async function askToSubmit() {
    if (!validate()) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    showSubmitConfirm.value = true;
}

async function submit() {
    if (submitting.value) {
        return;
    }
    submitting.value = true;
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/submit`).post().json();
        if (fetchError.value) {
            showSubmitConfirm.value = false;
            error.value = (data.value as { error?: string })?.error || "Could not submit.";
            return;
        }
        showSubmitConfirm.value = false;
        sending.value = true;
        emit("submitted");
    } finally {
        submitting.value = false;
    }
}

// The grouped renderer submits the response itself.
function onGroupedSubmitted() {
    submitted.value = true;
    emit("submitted");
}
</script>

<style scoped>
.submitted-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 9999px;
    background: #bee59f;
    color: #1e3f22;
    font-size: 32px;
}
</style>
